import React, { useState, useRef } from "react";
import Chart from "chart.js/auto";
import "../assets/styles/financial-independence.css";

const FinancialIndependence = () => {
  const [inputs, setInputs] = useState({
    currentAge: "",
    annualIncome: "",
    savingsRate: 20,
    annualSavings: "",
    currentPortfolio: "",
    rateOfReturn: 7.5,
    targetRetirementAge: "",
    desiredWithdrawal: "",
    safeWithdrawalRate: 4,
  });

  const [resultMessage, setResultMessage] = useState("");
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  const formatNumber = (value) =>
    value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const parseNumber = (value) => value.replace(/,/g, "");

  const updateField = (key, value) => {
    const cleanValue = parseFloat(parseNumber(value)) || "";
    setInputs((prev) => ({
      ...prev,
      [key]: cleanValue,
      ...(key === "savingsRate" && {
        annualSavings: cleanValue
          ? (cleanValue / 100) * (prev.annualIncome || 0)
          : "",
      }),
      ...(key === "annualIncome" && {
        annualSavings: (prev.savingsRate / 100) * (cleanValue || 0),
      }),
    }));
  };

  const calculateFI = () => {
    const {
      currentAge,
      targetRetirementAge,
      annualIncome,
      savingsRate,
      annualSavings,
      currentPortfolio,
      rateOfReturn,
      desiredWithdrawal,
      safeWithdrawalRate,
    } = inputs;

    if (
      !currentAge ||
      !targetRetirementAge ||
      !annualIncome ||
      !currentPortfolio ||
      !desiredWithdrawal
    ) {
      setResultMessage("Please fill in all required fields.");
      return;
    }

    const fiTarget = desiredWithdrawal / (safeWithdrawalRate / 100);
    const yearsToRetirement = targetRetirementAge - currentAge;
    const annualSavingsAmount = annualIncome * (savingsRate / 100);
    let cumulativeSavings = currentPortfolio || 0;
    let fiAchievedAge = null;

    const labels = Array.from(
      { length: yearsToRetirement + 1 },
      (_, i) => parseInt(currentAge) + i
    );
    const netWorthData = [];
    const fireTargetLine = Array(yearsToRetirement + 1).fill(fiTarget);

    for (let i = 0; i <= yearsToRetirement; i++) {
      cumulativeSavings += annualSavingsAmount;
      cumulativeSavings *= 1 + rateOfReturn / 100;

      if (!fiAchievedAge && cumulativeSavings >= fiTarget) {
        fiAchievedAge = parseInt(currentAge) + i;
      }
      netWorthData.push(Math.round(cumulativeSavings));
    }

    if (fiAchievedAge) {
      setResultMessage(
        `Congratulations! You will achieve financial independence at age ${fiAchievedAge}.`
      );
    } else {
      setResultMessage(
        `You will not achieve financial independence by age ${targetRetirementAge}.`
      );
    }

    renderChart(labels, netWorthData, fireTargetLine);
  };

  const renderChart = (labels, netWorthData, fireTargetLine) => {
    if (chartInstance) {
      chartInstance.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    const newChartInstance = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Net Worth",
            data: netWorthData,
            borderColor: "#95D1FC",
            backgroundColor: "rgba(149, 209, 252, 0.5)",
            fill: true,
          },
          {
            label: "FIRE Target",
            data: fireTargetLine,
            borderColor: "#FF5733",
            borderDash: [5, 5],
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          title: {
            display: true,
            text: "Net Worth Progression",
          },
        },
        scales: {
          y: { title: { display: true, text: "Net Worth ($)" } },
          x: { title: { display: true, text: "Age" } },
        },
      },
    });

    setChartInstance(newChartInstance);
  };

  return (
    <div className="fi-container">
      <section className="fi-hero">
        <h1>Financial Independence Calculator</h1>
        <p>
          Determine the age when you can achieve financial independence.
          Input your savings, income, and retirement goals to get started.
        </p>
      </section>

      <div className="fi-grid">
        <div className="fi-block">
          <h2 className="fi-block-title">Personal Information</h2>
          <div className="form-group">
            <label>Current Age:</label>
            <input
              type="number"
              value={inputs.currentAge}
              onChange={(e) => updateField("currentAge", e.target.value)}
            />
          </div>
        </div>

        <div className="fi-block">
          <h2 className="fi-block-title">Income and Savings</h2>
          <div className="form-group">
            <label>Annual Income ($):</label>
            <input
              type="text"
              value={formatNumber(inputs.annualIncome)}
              onChange={(e) => updateField("annualIncome", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Savings Rate (%):</label>
            <input
              type="range"
              min="0"
              max="100"
              value={inputs.savingsRate}
              onChange={(e) => updateField("savingsRate", e.target.value)}
            />
            <span>{inputs.savingsRate}%</span>
          </div>
          <div className="form-group">
            <label>Annual Savings Amount ($):</label>
            <input
              type="text"
              value={formatNumber(inputs.annualSavings)}
              onChange={(e) => updateField("annualSavings", e.target.value)}
            />
          </div>
        </div>

        <div className="fi-block">
          <h2 className="fi-block-title">Investment Details</h2>
          <div className="form-group">
            <label>Current Portfolio Value ($):</label>
            <input
              type="text"
              value={formatNumber(inputs.currentPortfolio)}
              onChange={(e) => updateField("currentPortfolio", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Assumed Rate of Return (%):</label>
            <input
              type="range"
              min="0"
              max="20"
              step="0.5"
              value={inputs.rateOfReturn}
              onChange={(e) => updateField("rateOfReturn", e.target.value)}
            />
            <span>{inputs.rateOfReturn}%</span>
          </div>
        </div>

        <div className="fi-block">
          <h2 className="fi-block-title">Retirement Goals</h2>
          <div className="form-group">
            <label>Target Retirement Age:</label>
            <input
              type="number"
              value={inputs.targetRetirementAge}
              onChange={(e) =>
                updateField("targetRetirementAge", e.target.value)
              }
            />
          </div>
          <div className="form-group">
            <label>Desired Annual Withdrawal ($):</label>
            <input
              type="text"
              value={formatNumber(inputs.desiredWithdrawal)}
              onChange={(e) => updateField("desiredWithdrawal", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Safe Withdrawal Rate (%):</label>
            <input
              type="range"
              min="2"
              max="6"
              value={inputs.safeWithdrawalRate}
              onChange={(e) => updateField("safeWithdrawalRate", e.target.value)}
            />
            <span>{inputs.safeWithdrawalRate}%</span>
          </div>
        </div>
      </div>

      <button className="calculate-button" onClick={calculateFI}>
        Calculate
      </button>
      <p className="fi-result">{resultMessage}</p>
      <div className="chart-container">
        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default FinancialIndependence;
